import { Loader } from 'google-maps';

// Baked in at build time by webpack's DefinePlugin from the .env.{BUILD_ENV}
// file at the repo root. Falls back to the customer-owned Valtech key when
// nothing is set — so production builds work out of the box. Local dev
// overrides via .env.development (Blastic dev key locked to localhost).
//
// Customer note: the fallback key lives in the Valtech GCP project. Places
// API (New) must be enabled on that project + EEA TOS accepted + HTTP
// referrer restrictions configured before this works in production.
// See .claude/guidelines/google-maps-api-key.md.
const API_KEY = process.env.GOOGLE_MAPS_API_KEY
  || 'AIzaSyChfqIRjRy1LbA5J4EUsKuCpNpAXovJsQk';

/**
 * Two discovery mechanisms:
 *
 * 1. SITECORE FORMS AUTO-DISCOVERY (primary, zero backend touch)
 *    Finds any <form> containing Sitecore-rendered fields with
 *    `data-sc-field-name="Country"` + `"Postal Code"`. Postcode is the single
 *    autocomplete trigger (a merged Postcode/City search after the HYUN-576
 *    UX redesign). City and County are read-only back-fill targets, populated
 *    on selection. No form-authoring, no Sitecore config, no CSS classes required.
 *
 * 2. DATA-ATTRIBUTE ESCAPE HATCH (custom / non-Sitecore forms)
 *    data-address-autocomplete          - Enables autocomplete on this input
 *    data-autocomplete-country="gb"     - Restrict to country (ISO 3166-1 alpha-2)
 *    data-autocomplete-fields='{...}'   - JSON mapping: Google type → selector
 */
const SELECTOR_DATA = '[data-address-autocomplete]';

/**
 * Candidate `data-sc-field-name` values for each role.
 * Matched in order; first hit wins. Add synonyms here when encountering
 * new field naming in other forms (CE vs MH, localized field names, etc.).
 */
const FIELD_NAMES = {
  country: ['Country'],
  postcode: ['Postal Code', 'Postcode', 'PostalCode', 'Zip Code', 'ZIP Code', 'Zip'],
  city: ['City', 'Town'],
  county: ['County', 'State', 'Province', 'Region'],
};

/**
 * Language code for Places API requests. Taken from the page's <html lang>
 * attribute so suggestions and filled values match the site language (e.g.
 * "de" on a German Sitecore page → "Köln" not "Keulen" or "Cologne").
 * Falls back to "en" if the attribute is missing or empty.
 */
const PAGE_LANGUAGE = (document.documentElement.lang || 'en').split('-')[0].toLowerCase() || 'en';

const POLL_INTERVAL = 100;
const POLL_TIMEOUT = 10000;
const DEBOUNCE_MS = 300;

// Min characters before firing autocomplete. Postcodes are denser per char
// (e.g. UK "SW" narrows to SW-London, BE "10" to Brussels) so 2 is fine for
// the merged Postcode/City search field. The data-attribute escape hatch
// uses the default (3).
const MIN_INPUT_LENGTH_POSTCODE = 2;
const MIN_INPUT_LENGTH_DEFAULT = 3;

/** Marker attribute to avoid double-enhancing after Sitecore AJAX re-render. */
const ENHANCED_MARK = 'data-hce-autocomplete-enhanced';

/**
 * UK uses `postal_town` instead of `locality` for city names — try locality
 * first, fall back to postal_town.
 */
const CITY_FALLBACK_TYPES = ['locality', 'postal_town'];

/**
 * Normalize a country name for forgiving lookup. Collapses common
 * cross-source variants so the ACC list, Intl.DisplayNames output, and
 * miscellaneous user input all map to the same key.
 *
 * Transforms applied (in order):
 *   - lowercase
 *   - NFD-strip diacritics ("Köln" → "koln", "Türkiye" → "turkiye")
 *   - "St." / "St " → "Saint " (Intl uses "St.", ACC uses "Saint")
 *   - " & " → " and " (Intl modern CLDR uses "&", ACC uses "and")
 *   - strip parenthetical clarifiers ("(Malvinas)", "(ROC)")
 *   - collapse whitespace
 */
function normalizeForCountryMatch(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[‘’]/g, "'")              // smart quotes → ASCII apostrophe
    .replace(/\bst\.?\s+/g, 'saint ')
    .replace(/\s+&\s+/g, ' and ')
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build a full English-name → ISO-2 map at runtime using the browser's
 * Intl.DisplayNames. Avoids shipping a 250-entry static list while still
 * covering every ISO 3166-1 country. Supported in all modern browsers
 * (Chrome 81+, Firefox 86+, Safari 14+).
 *
 * Keys are normalized via `normalizeForCountryMatch` so the same Intl
 * output matches whether the input came in as "United Kingdom",
 * "united kingdom", or with stray punctuation/diacritics.
 */
function buildIntlCountryMap() {
  const map = {};
  if (typeof Intl === 'undefined' || !Intl.DisplayNames) return map;
  let dn;
  try {
    dn = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch (e) {
    return map;
  }
  for (let i = 65; i <= 90; i++) {
    for (let j = 65; j <= 90; j++) {
      const code = String.fromCharCode(i) + String.fromCharCode(j);
      try {
        const name = dn.of(code);
        if (name && name !== code) {
          map[normalizeForCountryMatch(name)] = code.toLowerCase();
        }
      } catch (e) { /* unsupported code */ }
    }
  }
  return map;
}

/**
 * Country names whose ACC labels (Hyundai's Sitecore datasource) differ
 * semantically from Intl.DisplayNames output — Intl uses "South Korea",
 * ACC uses "Korea, Republic of"; Intl uses "Vatican City", ACC uses "Holy
 * See (Vatican City State)"; etc. Where normalization in
 * `normalizeForCountryMatch` already bridges the gap (diacritics, "&"↔"and",
 * "St."↔"Saint", parenthetical clarifiers) no entry is needed here.
 *
 * Keys are written in ACC's canonical form for readability; they're passed
 * through normalization before being merged into COUNTRY_NAME_TO_CODE.
 *
 * Maintenance note: if a future ACC list rename, CLDR update, or new
 * country breaks autocomplete (you'll see "Unrecognised country value" in
 * the console), add a single line here.
 */
const RAW_COUNTRY_OVERRIDES = {
  // No-Intl-equivalent edge case
  'Kosovo': 'xk',                  // no official ISO-2; Google accepts "xk"

  // Existing Sitecore-specific names
  'The Netherlands': 'nl',         // Intl: "Netherlands"
  'Czech Republic': 'cz',          // Intl: "Czechia"

  // ISO-2 collision pins. Intl.DisplayNames returns the SAME English name for
  // an active ISO-2 code and a deleted/transitional alias. AA→ZZ iteration in
  // buildIntlCountryMap lets the alphabetically-later (alias) code overwrite
  // the active one in the name→code map. The Places API then receives an
  // invalid region code (e.g. "fx", "hv") and silently drops the region
  // filter — returning worldwide results. Pin the canonical code per market:
  'Russia': 'ru',                  // wins over SU (Soviet Union, deleted)
  'United Kingdom': 'gb',          // wins over UK (non-ISO alias)
  'France': 'fr',                  // wins over FX (France Metropolitan, deleted 1997)
  'Burkina Faso': 'bf',            // wins over HV (Upper Volta, deleted)
  'Benin': 'bj',                   // wins over DY (Dahomey, deleted)
  'Serbia': 'rs',                  // wins over CS/YU (Serbia and Montenegro / Yugoslavia, deleted)
  'Timor-Leste': 'tl',             // wins over TP (Portuguese Timor, deleted)

  // "X, Y of" formal-name patterns where Intl flattens the name differently
  'Iran, Islamic Republic of': 'ir',                            // Intl: "Iran"
  "Korea, Democratic People's Republic of": 'kp',               // Intl: "North Korea"
  'Korea, Republic of': 'kr',                                   // Intl: "South Korea"
  'Macedonia, The Former Yugoslav Republic of': 'mk',           // Intl: "North Macedonia"
  'Micronesia, Federated States of': 'fm',                      // Intl: "Micronesia"
  'Moldova, Republic of': 'md',                                 // Intl: "Moldova"
  'Palestinian Territory, Occupied': 'ps',                      // Intl: "Palestinian Territories"
  'Tanzania, United Republic of': 'tz',                         // Intl: "Tanzania"
  'Virgin Islands, British': 'vg',                              // Intl: "British Virgin Islands"
  'Virgin Islands, U.S.': 'vi',                                 // Intl: "U.S. Virgin Islands"
  'Congo, The Democratic Republic of The': 'cd',                // Intl: "Congo - Kinshasa"

  // Names where Intl uses a colloquial / shortened form
  'Brunei Darussalam': 'bn',       // Intl: "Brunei"
  'Cape Verde': 'cv',              // Intl: "Cabo Verde"
  'Congo': 'cg',                   // Intl: "Congo - Brazzaville"
  'Hong Kong': 'hk',               // Intl: "Hong Kong SAR China"
  "Lao People's Democratic Republic": 'la',  // Intl: "Laos"
  'Libyan Arab Jamahiriya': 'ly',  // Intl: "Libya"
  'Macao': 'mo',                   // Intl: "Macao SAR China"
  'Pitcairn': 'pn',                // Intl: "Pitcairn Islands"
  'Syrian Arab Republic': 'sy',    // Intl: "Syria"
  'Turkey': 'tr',                  // Intl: "Türkiye"
  'United States Minor Outlying Islands': 'um',  // Intl: "U.S. Outlying Islands"

  // Stray-word mismatches Intl doesn't share with ACC's labels
  'Heard Island and Mcdonald Islands': 'hm',                    // Intl: "Heard & McDonald Islands"
  'Holy See (Vatican City State)': 'va',                        // Intl: "Vatican City"
  'Saint Vincent and The Grenadines': 'vc',                     // Intl: "St. Vincent & Grenadines"
  'South Georgia and The South Sandwich Islands': 'gs',         // Intl: "South Georgia & South Sandwich Islands"
};

const COUNTRY_NAME_OVERRIDES = Object.keys(RAW_COUNTRY_OVERRIDES).reduce((acc, key) => {
  acc[normalizeForCountryMatch(key)] = RAW_COUNTRY_OVERRIDES[key];
  return acc;
}, {});

const COUNTRY_NAME_TO_CODE = Object.assign(buildIntlCountryMap(), COUNTRY_NAME_OVERRIDES);

/**
 * Normalize a country value to ISO 3166-1 alpha-2 lowercase.
 * Accepts ISO-2 codes or English country names from COUNTRY_NAME_TO_CODE.
 */
function normalizeCountry(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (trimmed.length === 2) return trimmed.toLowerCase();
  const key = normalizeForCountryMatch(trimmed);
  if (COUNTRY_NAME_TO_CODE[key]) return COUNTRY_NAME_TO_CODE[key];
  // eslint-disable-next-line no-console
  console.warn(
    '[hce-address-autocomplete] Unrecognised country value:', value,
    '— extend RAW_COUNTRY_OVERRIDES if this is a supported market.'
  );
  return '';
}

/**
 * Dispatch native input + change events so Sitecore's jQuery validation
 * (unobtrusive validation) clears the error state on programmatic fill.
 */
function dispatchInputEvents(element) {
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Lowercase + strip diacritics for forgiving local search. "Köln" matches
 * "koln", "Türkiye" matches "turkiye", "Côte d'Ivoire" matches "cote".
 */
function normalizeForSearch(text) {
  return String(text || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

/**
 * Extract a value from address_components by type.
 * New Places API uses `longText` (not `long_name`).
 */
function getAddressComponent(components, type) {
  const component = components.find(c => c.types.includes(type));
  return component ? component.longText : '';
}

/**
 * Common non-ISO country abbreviations Google appends to postal codes.
 * Maps ISO-2 → list of alternate suffixes Google may use. The ISO code
 * itself is always accepted; this is for non-ISO conventions like "UK"
 * (Google's preferred suffix for GB postcodes).
 */
const COUNTRY_POSTCODE_SUFFIX_ALIASES = {
  GB: ['UK'],
};

/**
 * Strip a trailing country suffix from a postcode string.
 *
 * Google appends country information to postal_code longText in several
 * variants, depending on country and API version:
 *   - Belgium: "1000" or "1000 BE" or "1000 Belgium"
 *   - UK:     "SW1A 1AA" or "SW1A 1AA UK" or "SW1A 1AA United Kingdom"
 *   - NL:     "1012 AB" (letters ARE part of the postcode, don't touch)
 *
 * Strips trailing space/comma + a variant matching:
 *   - the country's ISO-2 code (BE, GB)
 *   - a non-ISO alias from COUNTRY_POSTCODE_SUFFIX_ALIASES (UK for GB)
 *   - the country's full longText (Belgium, United Kingdom)
 *
 * Leaves Dutch "1012 AB" untouched because "AB" matches none of the above.
 *
 * Shared by every postcode source: address_components.postal_code,
 * postal_code_prefix, and Geocoder fallback.
 */
function stripPostcodeCountrySuffix(raw, countryIso, countryName) {
  if (!raw) return raw;
  const iso = (countryIso || '').toUpperCase();
  const name = (countryName || '').trim();
  const aliases = COUNTRY_POSTCODE_SUFFIX_ALIASES[iso] || [];

  const variants = [];
  if (iso) variants.push(iso);
  variants.push.apply(variants, aliases);
  if (name) variants.push(name);

  for (let i = 0; i < variants.length; i++) {
    const v = variants[i];
    if (!v) continue;
    const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('[\\s,]+' + escaped + '$', 'i');
    const stripped = raw.replace(re, '').trim();
    if (stripped !== raw && stripped.length > 0) return stripped;
  }

  return raw;
}

/**
 * Extract and clean a postal_code from address_components.
 * Prefers shortText (Google's machine-readable form, typically clean) and
 * falls back to longText with country-suffix stripping.
 */
function extractCleanPostalCode(components) {
  const comp = components.find(c => c.types.includes('postal_code'));
  if (!comp) return '';
  const countryComp = components.find(c => c.types.includes('country'));
  const iso = countryComp ? countryComp.shortText : '';
  const name = countryComp ? countryComp.longText : '';
  const raw = (comp.shortText || comp.longText || '').trim();
  return stripPostcodeCountrySuffix(raw, iso, name);
}

/**
 * Extract city value with UK postal_town fallback.
 */
function getCityValue(components) {
  for (const type of CITY_FALLBACK_TYPES) {
    const value = getAddressComponent(components, type);
    if (value) return value;
  }
  return '';
}

/**
 * Geocoder-based postcode lookup as last-resort fallback for sublocality places
 * (e.g. Berchem, Antwerp) where Places Data API returns no postal_code in
 * addressComponents and no postcode in formattedAddress. Geocoding the
 * place's lat/lng centroid reliably returns postal_code ("2600" for Berchem).
 * Geocoder is part of the core Maps library — no extra import needed.
 */
function geocodePostcode(google, location) {
  return new Promise(resolve => {
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location }, (results, status) => {
        if (status !== 'OK' || !results || !results.length) {
          resolve('');
          return;
        }
        const allComps = results.flatMap(r => r.address_components || []);
        const pcComp = allComps.find(c => c.types.includes('postal_code'));
        const countryComp = allComps.find(c => c.types.includes('country'));
        // Geocoder uses short_name (machine code) and long_name (human label).
        const raw = pcComp ? (pcComp.short_name || pcComp.long_name || '') : '';
        const iso = countryComp ? countryComp.short_name : '';
        const name = countryComp ? countryComp.long_name : '';
        resolve(stripPostcodeCountrySuffix(raw, iso, name));
      });
    } catch (e) {
      resolve('');
    }
  });
}

/**
 * Last-resort postcode extraction: regex over Google's formatted address string.
 * Handles the common European formats when address_components lack `postal_code`
 * (e.g. Google returns sublocality-level places like Berchem without an
 * explicit postal_code component, but the formatted address still includes it).
 *
 * Order matters — more specific patterns first:
 *   - Dutch: "1012 AB" (4 digits + space + 2 letters)
 *   - UK:    "SW1A 1AA" (outward + inward)
 *   - Generic numeric: 4 or 5 digits (BE, DE, FR, ES, IT, PL, ...)
 */
function extractPostcodeFromFormatted(formattedAddress, countryIso) {
  if (!formattedAddress) return '';
  const iso = (countryIso || '').toUpperCase();

  // Dutch: "1012 AB" — 4 digits + space + 2 letters. If the 2 letters are
  // the country's own ISO code (e.g. "2000 BE" for Belgium), strip them.
  const nlMatch = formattedAddress.match(/\b(\d{4})\s([A-Z]{2})\b/);
  if (nlMatch) {
    return nlMatch[2] === iso ? nlMatch[1] : `${nlMatch[1]} ${nlMatch[2]}`;
  }

  // UK: "SW1A 1AA"
  const ukMatch = formattedAddress.match(/\b([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})\b/i);
  if (ukMatch) return ukMatch[1].toUpperCase();

  // Generic numeric 4-5 digits after a comma (postcode position, not street number)
  const numAfterComma = formattedAddress.match(/,\s*(\d{4,5})\b/);
  if (numAfterComma) return numAfterComma[1];

  return '';
}

function debounce(fn, ms) {
  let timer;
  return function () {
    clearTimeout(timer);
    const args = arguments;
    const context = this;
    timer = setTimeout(() => fn.apply(context, args), ms);
  };
}

/**
 * Find the first field in a form matching any of the candidate names.
 */
function findField(form, candidates) {
  for (const name of candidates) {
    const el = form.querySelector(`[data-sc-field-name="${name}"]`);
    if (el) return el;
  }
  return null;
}

/**
 * Discover Sitecore Forms instances that can host autocomplete.
 * A form qualifies if it has a Country field plus a Postal Code field.
 * City and County are optional back-fill targets.
 *
 * The HYUN-576 UX redesign (2026-05) merged the Postcode + City inputs
 * into a single search field that lives under `data-sc-field-name="Postal Code"`.
 * City and County are read-only display fields populated on selection.
 */
function discoverSitecoreForms() {
  const candidates = [];
  document.querySelectorAll('form').forEach(form => {
    const country = findField(form, FIELD_NAMES.country);
    if (!country) return;
    const postcode = findField(form, FIELD_NAMES.postcode);
    if (!postcode) return;
    const city = findField(form, FIELD_NAMES.city);
    const county = findField(form, FIELD_NAMES.county);
    candidates.push({ form, country, postcode, city, county });
  });
  return candidates;
}

/* eslint-disable max-len */
const ICON_SEARCH = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>';
const ICON_CLEAR = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
/* eslint-enable max-len */

/**
 * Wrap the input with search and clear icons.
 */
function wrapWithIcons(input) {
  const wrapper = document.createElement('div');
  wrapper.className = 'hce-address-input-wrapper';

  const searchIcon = document.createElement('span');
  searchIcon.className = 'hce-address-icon hce-address-icon--search';
  searchIcon.innerHTML = ICON_SEARCH;

  const clearBtn = document.createElement('span');
  clearBtn.className = 'hce-address-icon hce-address-icon--clear';
  clearBtn.innerHTML = ICON_CLEAR;
  clearBtn.style.display = 'none';

  input.parentNode.insertBefore(wrapper, input);
  wrapper.appendChild(searchIcon);
  wrapper.appendChild(input);
  wrapper.appendChild(clearBtn);

  input.style.paddingLeft = '44px';
  input.style.paddingRight = '40px';

  function updateClearVisibility() {
    clearBtn.style.display = input.value.length > 0 ? '' : 'none';
  }

  input.addEventListener('input', updateClearVisibility);

  clearBtn.addEventListener('mousedown', e => {
    e.preventDefault();
    input.value = '';
    dispatchInputEvents(input);
    input.dispatchEvent(new CustomEvent('address-clear'));
    updateClearVisibility();
    input.focus();
  });

  return { updateClearVisibility };
}

/**
 * English country longName from an ISO-2 code via Intl.DisplayNames.
 * "GB" → "United Kingdom"; "BE" → "Belgium". Returns '' if unavailable.
 */
function getCountryNameForIso(iso) {
  if (!iso || typeof Intl === 'undefined' || !Intl.DisplayNames) return '';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(String(iso).toUpperCase()) || '';
  } catch (e) {
    return '';
  }
}

/**
 * Pull a postcode + remainder out of a free-text string for the given country.
 * Handles cases where Google embeds postcode together with a city/area name
 * in structuredFormat.mainText (notably UK: "London SW11", "Birmingham B1").
 *
 * Returns { postcode, rest } when a postcode pattern is found, else null.
 *
 * UK uses the alphanumeric pattern (SW1A, NG31 6, B1 1AA, ...). Other
 * supported markets use 4-5 digit numeric postcodes (BE, DE, FR, ES, IT,
 * PL, ...). NL "1012 AB" stays as-is because mainText for NL postcode
 * predictions is typically just the postcode with no city embedded.
 */
function extractPostcodeFromMainText(text, iso) {
  if (!text) return null;

  function partition(match) {
    const before = text.slice(0, match.index);
    const after = text.slice(match.index + match[0].length);
    // Stitch around the postcode, preserve commas (caller uses them as
    // segment boundaries), and clean orphan whitespace/commas at the edges.
    const rest = (before + after)
      .replace(/\s+/g, ' ')
      .replace(/^[\s,]+|[\s,]+$/g, '');
    return { postcode: match[1] || match[0], rest };
  }

  // Accept both 'GB' (correct ISO-2) and 'UK' (non-ISO alias Intl.DisplayNames
  // collides into the same English longName) so this stays robust if the
  // name→code map ever flips back.
  if (iso === 'GB' || iso === 'UK') {
    const uk = text.match(/\b([A-Z]{1,2}\d[A-Z\d]?(?:\s?\d[A-Z]{2})?)\b/i);
    if (uk) {
      const result = partition(uk);
      result.postcode = result.postcode.toUpperCase();
      return result;
    }
  }

  const num = text.match(/\b(\d{4,5})\b/);
  if (num) return partition(num);

  return null;
}

/**
 * Factory: render a Places autocomplete prediction as "{postcode} - {city}"
 * (or "{postcode} - {street} - {city}" when Google's text includes a
 * street-level segment ahead of the postcode).
 *
 * County is NOT shown in the dropdown row — Google's autocomplete
 * predictions don't carry administrative_area data, only Place Details does
 * (one per-place call billed on selection). Instead, picking a row fills the
 * County form field below via the back-fill logic in enhanceField (uses
 * address_components.administrative_area_level_2).
 *
 * Steps for postcode-typed predictions:
 *   1. Build a source string from structuredFormat (if present) or pred.text
 *      (most postcode predictions ship structuredFormat=null), then strip
 *      every country variant (ISO-2, Intl longName, alias like UK↔GB).
 *   2. Extract a postcode pattern from the text — Google often embeds the
 *      postcode together with the city ("London SW11" for UK), so we pull
 *      the postcode out and use the rest as the city part.
 *   3. Build a parts array [postcode, ...remainingSegments] with
 *      case-insensitive dedup. Join with " - ".
 *
 * Examples:
 *   - GB: text="London SW11, UK"                        → "SW11 - London"
 *   - GB: text="London SW11 7US, UK"                    → "SW11 7US - London"
 *   - GB: text="Saint John's Hill, London SW11 1TT, UK" → "SW11 1TT - Saint John's Hill - London"
 *   - BE: text="1000, Brussels, Belgium"                → "1000 - Brussels"
 *
 * Locality-typed predictions (Antwerp, London) carry no postcode at
 * suggestion time. City rows render as bare mainText.
 */
function makeSuggestionFormatter(getCountry) {
  function partsContains(parts, value) {
    const v = value.toLowerCase();
    return parts.some(p => p.toLowerCase() === v);
  }

  return function formatSuggestionAsPostcodeCity(suggestion) {
    const pred = suggestion.placePrediction;
    const types = (pred.types || []).map(t => String(t).toLowerCase());
    const structured = pred.structuredFormat || {};

    const iso = (getCountry() || '').toUpperCase();
    const name = getCountryNameForIso(iso);

    // Build the working text:
    //   - if structuredFormat is populated, prefer it (mainText + ", " + secondaryText)
    //   - else fall back to pred.text (Google ships structuredFormat=null for many
    //     postcode predictions, e.g. UK "London SW11, UK")
    // Either way, run through country-suffix strip so "UK"/"Belgium"/etc are removed.
    let source = '';
    const rawMain = structured.mainText ? structured.mainText.toString() : '';
    const rawSecondary = structured.secondaryText ? structured.secondaryText.toString() : '';
    if (rawMain || rawSecondary) {
      const mainText = stripPostcodeCountrySuffix(rawMain, iso, name);
      const secondaryText = stripPostcodeCountrySuffix(rawSecondary, iso, name);
      source = mainText && secondaryText
        ? `${mainText}, ${secondaryText}`
        : (mainText || secondaryText);
    } else if (pred.text) {
      source = stripPostcodeCountrySuffix(pred.text.toString(), iso, name);
    }

    const isPostcode = types.indexOf('postal_code') !== -1
                    || types.indexOf('postal_code_prefix') !== -1;

    if (isPostcode) {
      const extracted = extractPostcodeFromMainText(source, iso);
      const postcode = extracted ? extracted.postcode : '';
      const remainder = extracted ? extracted.rest : source;
      const remainderParts = remainder.split(',').map(s => s.trim()).filter(Boolean);

      const parts = [];
      if (postcode) parts.push(postcode);
      for (let i = 0; i < remainderParts.length; i++) {
        if (!partsContains(parts, remainderParts[i])) parts.push(remainderParts[i]);
      }
      if (parts.length > 0) return parts.join(' - ');
    }

    return source || (pred.text ? pred.text.toString() : '');
  };
}

/**
 * Monotonic counter for generating unique IDs on dropdown ul + li elements,
 * referenced from the input's aria-controls + aria-activedescendant. The
 * IDs need to be stable enough for assistive tech to follow updates and
 * unique across multiple instances on the same page.
 */
let dropdownIdCounter = 0;

/**
 * Create and manage the suggestions dropdown for an input.
 *
 *   formatSuggestion — optional (suggestion) => string. Defaults to the raw
 *                      `placePrediction.text` so the data-attribute street-
 *                      address path keeps Google's full formatted address.
 *
 * ARIA: the dropdown is wired as a combobox + listbox pattern so screen
 * readers announce the input as a search field, the dropdown as a list,
 * and the active item as the currently-highlighted option. Pattern follows
 * https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
 */
function createDropdown(input, formatSuggestion) {
  const renderText = formatSuggestion
    || (suggestion => suggestion.placePrediction.text.toString());

  dropdownIdCounter += 1;
  const listId = 'hce-address-dropdown-' + dropdownIdCounter;

  const dropdown = document.createElement('ul');
  dropdown.className = 'hce-address-dropdown';
  dropdown.id = listId;
  dropdown.setAttribute('role', 'listbox');
  dropdown.style.display = 'none';

  const wrapper = input.parentNode;
  if (getComputedStyle(wrapper).position === 'static') {
    wrapper.style.position = 'relative';
  }
  wrapper.appendChild(dropdown);

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', listId);

  let activeIndex = -1;
  let items = [];

  function show(suggestions) {
    items = suggestions;
    activeIndex = -1;
    dropdown.innerHTML = '';
    input.removeAttribute('aria-activedescendant');

    if (!suggestions.length) {
      dropdown.style.display = 'none';
      input.setAttribute('aria-expanded', 'false');
      return;
    }

    suggestions.forEach((suggestion, i) => {
      const li = document.createElement('li');
      li.className = 'hce-address-dropdown__item';
      li.id = listId + '-opt-' + i;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.textContent = renderText(suggestion);
      li.addEventListener('mousedown', e => {
        e.preventDefault();
        select(i);
      });
      dropdown.appendChild(li);
    });

    dropdown.style.display = '';
    input.setAttribute('aria-expanded', 'true');
    positionDropdown();
  }

  /**
   * Decide whether to open the dropdown below (default) or above the input.
   * Flips above when there isn't enough room below AND there's more room
   * above. On touch devices, deducts an estimate of the soft keyboard
   * footprint (~half the viewport) so the dropdown doesn't open into the
   * keyboard's overlay area.
   */
  function positionDropdown() {
    dropdown.classList.remove('hce-address-dropdown--flipped');

    const inputRect = input.getBoundingClientRect();
    const dropdownHeight = Math.min(dropdown.scrollHeight, 280);
    let spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    // Touch heuristic: assume the soft keyboard occupies ~50% of the
    // viewport while the input is focused. visualViewport tracks this more
    // accurately on modern browsers but updates async and isn't universal,
    // so the constant estimate is a more reliable trigger.
    const isTouch = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    if (isTouch) {
      spaceBelow -= window.innerHeight * 0.5;
    }

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      dropdown.classList.add('hce-address-dropdown--flipped');
    }
  }

  function hide() {
    dropdown.style.display = 'none';
    activeIndex = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  function highlight(index) {
    const children = dropdown.children;
    for (let i = 0; i < children.length; i++) {
      const isActive = i === index;
      children[i].classList.toggle('hce-address-dropdown__item--active', isActive);
      children[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
    activeIndex = index;
    const activeChild = dropdown.children[index];
    if (activeChild) {
      input.setAttribute('aria-activedescendant', activeChild.id);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function select(index) {
    if (items[index]) {
      input.dispatchEvent(new CustomEvent('suggestion-select', { detail: items[index] }));
    }
    hide();
  }

  input.addEventListener('keydown', e => {
    if (dropdown.style.display === 'none' || !items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlight(activeIndex < items.length - 1 ? activeIndex + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlight(activeIndex > 0 ? activeIndex - 1 : items.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0) select(activeIndex);
    } else if (e.key === 'Escape') {
      hide();
    }
  });

  input.addEventListener('blur', () => setTimeout(hide, 150));

  return { show, hide };
}

/**
 * Fill a target input if we have a value. Doesn't clobber with an empty string
 * — cross-field back-fill is additive, not destructive.
 *
 * If the target is itself an autocomplete-enhanced input (e.g. City while
 * the user triggered from Postcode), mark it so its own `input` listener
 * skips the next query. Without this, back-filling City re-opens the City
 * dropdown with its just-filled value → user picks it → back-fills Postcode
 * → re-opens Postcode dropdown → infinite toggle.
 */
function fillIfPresent(target, value) {
  if (!target || !value) return;
  if (target.hasAttribute(ENHANCED_MARK)) {
    target.__hceSkipNext = true;
  }
  target.value = value;
  dispatchInputEvents(target);
}

/**
 * Attach autocomplete behaviour to a single trigger input.
 *
 *   input          — the input that gets the dropdown
 *   primaryTypes   — Google Places primary-type filter (e.g. ['postal_code'])
 *   getCountry     — () => ISO-2 country code, read live on each query
 *   selfExtract    — (addressComponents) => value to place in `input` on selection
 *   targets        — { postcode?, city?, county? } DOM refs, filled on selection
 *   minLength      — minimum chars before a query fires (default 3)
 *   afterSelect    — optional (components, prevSelfValue, place) => void hook,
 *                    runs after the standard self-fill + back-fill is complete
 *   formState      — optional shared state across all enhanced fields of a form
 *                    (currently just `filledIso` for country-confirm short-circuit)
 */
function enhanceField(google, input, primaryTypes, getCountry, selfExtract, targets, minLength, afterSelect, formState) {
  const minInputLength = minLength || MIN_INPUT_LENGTH_DEFAULT;
  if (input.hasAttribute(ENHANCED_MARK)) return;
  input.setAttribute(ENHANCED_MARK, '1');

  const { updateClearVisibility } = wrapWithIcons(input);
  input.setAttribute('autocomplete', 'off');

  const AutocompleteSuggestion = google.maps.places.AutocompleteSuggestion;
  const AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;

  let sessionToken = new AutocompleteSessionToken();
  const dropdown = createDropdown(input, makeSuggestionFormatter(getCountry));

  // skipNext flag lives on the input element (not this closure) so that
  // cross-field back-fill — via `fillIfPresent` — can suppress this input's
  // next query by setting `target.__hceSkipNext = true` before dispatching.
  const fetchSuggestions = debounce(async () => {
    if (input.__hceSkipNext) {
      input.__hceSkipNext = false;
      return;
    }

    const query = input.value.trim();
    if (query.length < minInputLength) {
      dropdown.hide();
      return;
    }

    const request = { input: query, sessionToken };
    if (primaryTypes && primaryTypes.length) {
      request.includedPrimaryTypes = primaryTypes;
    }

    const country = getCountry();
    if (country) {
      request.includedRegionCodes = [country];
    }

    try {
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      dropdown.show(suggestions || []);
    } catch (e) {
      console.warn('[hce-address-autocomplete] fetchAutocompleteSuggestions failed:', e);
      dropdown.hide();
    }
  }, DEBOUNCE_MS);

  input.addEventListener('input', fetchSuggestions);

  input.addEventListener('suggestion-select', async (e) => {
    const suggestion = e.detail;
    const place = suggestion.placePrediction.toPlace();

    try {
      await place.fetchFields({ fields: ['addressComponents', 'location', 'formattedAddress'] });
    } catch (err) {
      console.warn('[hce-address-autocomplete] fetchFields failed:', err);
      return;
    }

    const components = place.addressComponents;
    if (!components) return;

    const prevSelfValue = input.value;

    // Extract country early — needed by postcode extraction waterfall.
    const countryComp = components.find(c => c.types.includes('country'));
    const filledIso = countryComp ? countryComp.shortText.toLowerCase() : null;
    const filledCountryName = countryComp ? countryComp.longText : '';

    // Resolve best postcode for this place using a waterfall:
    //   1. postal_code in addressComponents (most reliable)
    //   2. postal_code_prefix  (UK postal_towns: "NG31")
    //   3. Regex on formattedAddress  (catches numeric postcodes e.g. "2600")
    //   4. Geocoder reverse-lookup by lat/lng  (sublocalities like Berchem
    //      where Places API returns no postcode at all but Geocoder does)
    let resolvedPostcode = extractCleanPostalCode(components)
                        || stripPostcodeCountrySuffix(
                             getAddressComponent(components, 'postal_code_prefix'),
                             filledIso,
                             filledCountryName,
                           )
                        || extractPostcodeFromFormatted(place.formattedAddress, filledIso);

    if (!resolvedPostcode && place.location) {
      resolvedPostcode = await geocodePostcode(google, {
        lat: typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat,
        lng: typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng,
      });
    }

    // Self-fill — triggering input gets its own specific component.
    // Convention for selfExtract return values:
    //   - non-empty string → set as input value
    //   - empty string ""  → clear input (user's search text is now obsolete)
    //   - null/undefined   → leave input alone
    // Postcode extract returns "" when Google has no postal_code/prefix, so
    // typing a city name in Postcode then picking a locality doesn't leave
    // "Herve" or similar city text stranded in Postcode.
    const selfValue = selfExtract(components, place, resolvedPostcode);
    if (selfValue !== null && selfValue !== undefined) {
      input.__hceSkipNext = true;
      input.value = selfValue;
      dispatchInputEvents(input);
    }

    // Cross-field back-fill (additive — only writes non-empty values).
    // Skip the trigger field itself: its value was already set by self-fill
    // above and cross-fill would otherwise overwrite specific matches (e.g.
    // a Berchem sublocality pick) with the generic locality (Antwerp).
    if (targets.postcode !== input) {
      fillIfPresent(targets.postcode, resolvedPostcode);
    }
    if (targets.city !== input) {
      fillIfPresent(targets.city, getCityValue(components));
    }
    if (targets.county !== input) {
      fillIfPresent(targets.county, getAddressComponent(components, 'administrative_area_level_2'));
    }

    // Track the ISO code so the country change listener can skip clearing
    // when the user confirms the same country afterwards.
    if (formState) formState.filledIso = filledIso;

    // Auto-select Country when it's empty: find the select option whose
    // normalised value matches the place's country ISO code (BE → "Belgium").
    if (filledIso && targets.country && !targets.country.value) {
      const match = Array.from(targets.country.options)
        .find(opt => normalizeCountry(opt.value) === filledIso);
      if (match) {
        targets.country.value = match.value;
        dispatchInputEvents(targets.country);
      }
    }

    if (afterSelect) afterSelect(components, prevSelfValue, place, resolvedPostcode);

    updateClearVisibility();
    sessionToken = new AutocompleteSessionToken();
  });

  input.addEventListener('address-clear', () => {
    // Clear all address fields except country (intentional user choice) and
    // the trigger input itself (already cleared by the clear button).
    [targets.postcode, targets.city, targets.county].forEach(field => {
      if (field && field !== input && field.value) {
        if (field.hasAttribute(ENHANCED_MARK)) field.__hceSkipNext = true;
        field.value = '';
        dispatchInputEvents(field);
      }
    });
    // Reset per-form state — context is no longer valid.
    if (formState) {
      formState.filledIso = null;
    }
    dropdown.hide();
    sessionToken = new AutocompleteSessionToken();
  });
}

/**
 * Upgrade a Sitecore Country <select> into a searchable text input.
 *
 * Hides the native <select> (still in the DOM so Sitecore's form POST sends
 * Country=<value>) and renders a text input above it that filters the option
 * list locally on each keystroke — diacritic-insensitive, prefix-first then
 * substring. On pick, the <select>'s value is set and a `change` event
 * dispatched so Sitecore's jQuery validation and `enhanceSitecoreForm`'s
 * country-change handler fire as before.
 *
 * Bidirectional sync:
 *   user picks in dropdown → input.value, select.value, dispatch change
 *   external code sets select.value → 'change' listener re-syncs input.value
 *     (covers the postcode-pick auto-fill that writes back to Country)
 *
 * Re-targets `label[for="<selectId>"]` to the new input so screen readers and
 * label-click-to-focus still work.
 */
function enhanceSitecoreCountrySelect(select) {
  if (select.tagName !== 'SELECT') return;
  if (select.hasAttribute(ENHANCED_MARK)) return;
  select.setAttribute(ENHANCED_MARK, '1');

  // Skip placeholder option (Sitecore renders `<option label=" "></option>`).
  const options = Array.from(select.options).filter(o => o.value && o.value.trim());
  if (options.length === 0) return;

  select.style.display = 'none';
  select.setAttribute('aria-hidden', 'true');
  select.setAttribute('tabindex', '-1');

  const input = document.createElement('input');
  input.type = 'text';
  input.className = select.className || 'form-control';
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('placeholder', 'Search country...');

  if (select.id) {
    input.id = select.id + '_search';
    const label = document.querySelector(`label[for="${CSS.escape(select.id)}"]`);
    if (label) label.setAttribute('for', input.id);
  }

  select.parentNode.insertBefore(input, select);
  const { updateClearVisibility } = wrapWithIcons(input);

  const indexed = options.map(opt => ({ opt, norm: normalizeForSearch(opt.textContent) }));
  function filterOptions(query) {
    const q = normalizeForSearch(query);
    if (!q) return indexed.map(i => i.opt);
    const prefix = [];
    const contains = [];
    indexed.forEach(i => {
      if (i.norm.startsWith(q)) prefix.push(i.opt);
      else if (i.norm.includes(q)) contains.push(i.opt);
    });
    return prefix.concat(contains);
  }

  const dropdown = createDropdown(input, opt => opt.textContent);

  // Seed with any pre-existing Sitecore value (e.g. server-side default).
  if (select.value) {
    const initial = select.selectedOptions[0];
    if (initial) input.value = initial.textContent;
    updateClearVisibility();
  }

  input.addEventListener('focus', () => dropdown.show(filterOptions(input.value)));
  input.addEventListener('input', () => {
    dropdown.show(filterOptions(input.value));
    updateClearVisibility();
  });

  input.addEventListener('suggestion-select', (e) => {
    const opt = e.detail;
    if (!opt) return;
    input.value = opt.textContent;
    select.value = opt.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    updateClearVisibility();
  });

  // Clear button (from wrapWithIcons) — reset the select and let downstream
  // listeners clear the address fields.
  input.addEventListener('address-clear', () => {
    select.value = '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Snap input back to the actual selected option on blur — handles the case
  // where the user typed something that doesn't match anything and tabbed away.
  input.addEventListener('blur', () => {
    setTimeout(() => {
      const current = select.selectedOptions[0];
      input.value = current && current.value ? current.textContent : '';
      updateClearVisibility();
    }, 200);
  });

  // External programmatic fills (postcode-pick → auto-fills country).
  select.addEventListener('change', () => {
    const current = select.selectedOptions[0];
    const newText = current && current.value ? current.textContent : '';
    if (input.value !== newText) {
      input.value = newText;
      updateClearVisibility();
    }
  });
}

/**
 * Enhance a discovered Sitecore form: attach autocomplete to the merged
 * Postcode/City search field. City and County are read-only back-fill targets.
 *
 * Shared per-form state (`formState`):
 *   filledIso — ISO-2 country of the last autocomplete selection. Used to
 *               skip clearing fields when the user confirms the auto-filled
 *               country by selecting it manually.
 */
function enhanceSitecoreForm(google, formConfig) {
  const { country, postcode, city, county } = formConfig;
  const getCountry = () => normalizeCountry(country ? country.value : '');
  const targets = { postcode, city, county, country };
  const formState = { filledIso: null };

  // Upgrade the native Country <select> to a searchable text input first,
  // so its 'change' listener (added below) sees the picker's dispatched events.
  if (country) {
    enhanceSitecoreCountrySelect(country);
  }

  // City and County are back-fill-only display fields — the user never types
  // in them, they're populated from the picked place. Enforce `readonly` here
  // rather than relying on Sitecore field config: standard Sitecore Forms has
  // no native read-only property on the Single-Line Text template, so the FE
  // owns this. `readonly` (not `disabled`) keeps the values in the form POST,
  // and programmatic back-fill (value + dispatched events) still works.
  [city, county].forEach(field => {
    if (field) {
      field.setAttribute('readonly', '');
      field.setAttribute('aria-readonly', 'true');
    }
  });

  // Postcode is read-only until a country is selected — Google's autocomplete
  // needs a region code to bias results, and submitting a postcode without a
  // country is rarely meaningful for the contact form. Original placeholder is
  // stashed in a dataset attr so we can restore it when the country fills in.
  function syncPostcodeAvailability() {
    if (!postcode) return;
    const hasCountry = !!(country && country.value && country.value.trim());
    if (hasCountry) {
      postcode.removeAttribute('readonly');
      postcode.removeAttribute('aria-disabled');
      if (postcode.dataset.hceOriginalPlaceholder !== undefined) {
        postcode.placeholder = postcode.dataset.hceOriginalPlaceholder;
      }
    } else {
      if (postcode.dataset.hceOriginalPlaceholder === undefined) {
        postcode.dataset.hceOriginalPlaceholder = postcode.placeholder || '';
      }
      postcode.setAttribute('readonly', '');
      postcode.setAttribute('aria-disabled', 'true');
      postcode.placeholder = 'Select a country first';
    }
  }
  syncPostcodeAvailability();

  // Changing the Country invalidates every address field when the new country
  // differs from the data already filled. If the user selects the same country
  // as the autocompleted data (confirming it), skip the clear.
  if (country) {
    country.addEventListener('change', () => {
      const selectedIso = normalizeCountry(country.value);
      const shouldClear = !(selectedIso && selectedIso === formState.filledIso);

      if (shouldClear) {
        formState.filledIso = null;
        [targets.postcode, targets.city, targets.county].forEach(field => {
          if (field && field.value) {
            if (field.hasAttribute(ENHANCED_MARK)) field.__hceSkipNext = true;
            field.value = '';
            dispatchInputEvents(field);
          }
        });
      }

      syncPostcodeAvailability();
    });
  }

  // Primary-type filter: only postcodes and city-level localities. Without
  // a filter, Google's Data API returns street addresses, businesses, and
  // POIs ("Sampson Road North", "Tesco Birmingham", ...) which aren't
  // selectable picks for a postcode/city search. Filtering to ['postal_code']
  // alone is too narrow (partial inputs like "SW1A" return zero) — including
  // locality types restores coverage while excluding routes/premises.
  //
  // Postcode is extracted from whichever place the user picks via the
  // fallback chain:
  //   1. full postal_code (best — e.g. "4650" for Herve, "SW1A 1AA")
  //   2. postal_code_prefix (partial seed — "NG31" for Grantham)
  //   3. Geocoder reverse-lookup by lat/lng (for sublocality picks)
  enhanceField(
    google, postcode,
    ['postal_code', 'locality', 'postal_town', 'sublocality'],
    getCountry,
    // resolvedPostcode is pre-computed by the suggestion-select handler
    // (including Geocoder fallback for sublocality places like Berchem).
    // Return it directly; empty string means "clear the user's typed query".
    (components, place, resolvedPostcode) => resolvedPostcode !== undefined ? resolvedPostcode : '',
    targets,
    MIN_INPUT_LENGTH_POSTCODE,
    null,
    formState,
  );

  // City and County are display-only fields owned by the most recent pick.
  // When the user starts typing a new query in postcode, the previously-
  // filled values are stale (e.g. "Surrey" lingering next to a half-typed
  // Belgian postcode). Clear them so the form never shows mismatched data.
  // Skip when the input event is a pick aftershock (enhanceField sets
  // postcode.__hceSkipNext before programmatically writing the picked value).
  if (postcode) {
    postcode.addEventListener('input', () => {
      if (postcode.__hceSkipNext) return;
      [targets.city, targets.county].forEach(field => {
        if (field && field.value) {
          field.value = '';
          dispatchInputEvents(field);
        }
      });
    });
  }
}

/**
 * Legacy data-attribute path: single input with a JSON target map,
 * using the suggestion text as the input's own value (legacy "address" mode).
 * Retained for non-Sitecore / custom forms.
 */
function enhanceLegacyInput(google, input) {
  if (input.hasAttribute(ENHANCED_MARK)) return;

  const staticCountry = normalizeCountry(input.dataset.autocompleteCountry || '');
  let fieldMapping = {};
  try {
    fieldMapping = JSON.parse(input.dataset.autocompleteFields || '{}');
  } catch (e) {
    console.warn('[hce-address-autocomplete] Invalid JSON in data-autocomplete-fields:', e);
    return;
  }

  input.setAttribute(ENHANCED_MARK, '1');

  const { updateClearVisibility } = wrapWithIcons(input);
  input.setAttribute('autocomplete', 'off');

  const AutocompleteSuggestion = google.maps.places.AutocompleteSuggestion;
  const AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
  let sessionToken = new AutocompleteSessionToken();
  let skipNextInput = false;
  const dropdown = createDropdown(input);

  const fetchSuggestions = debounce(async () => {
    if (skipNextInput) { skipNextInput = false; return; }
    const query = input.value.trim();
    if (query.length < MIN_INPUT_LENGTH_DEFAULT) { dropdown.hide(); return; }
    const request = { input: query, sessionToken };
    if (staticCountry) request.includedRegionCodes = [staticCountry];
    try {
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      dropdown.show(suggestions || []);
    } catch (e) {
      console.warn('[hce-address-autocomplete] fetchAutocompleteSuggestions failed:', e);
      dropdown.hide();
    }
  }, DEBOUNCE_MS);

  input.addEventListener('input', fetchSuggestions);

  input.addEventListener('suggestion-select', async (e) => {
    const place = e.detail.placePrediction.toPlace();
    try {
      await place.fetchFields({ fields: ['addressComponents'] });
    } catch (err) {
      console.warn('[hce-address-autocomplete] fetchFields failed:', err);
      return;
    }
    const components = place.addressComponents;
    if (!components) return;

    skipNextInput = true;
    input.value = e.detail.placePrediction.text.toString();
    dispatchInputEvents(input);

    const form = input.closest('form') || document;
    Object.keys(fieldMapping).forEach(googleType => {
      const target = form.querySelector(fieldMapping[googleType]);
      if (!target) return;
      const value = googleType === 'locality'
        ? getCityValue(components)
        : getAddressComponent(components, googleType);
      if (value) {
        target.value = value;
        dispatchInputEvents(target);
      }
    });

    updateClearVisibility();
    sessionToken = new AutocompleteSessionToken();
  });

  input.addEventListener('address-clear', () => {
    const form = input.closest('form') || document;
    Object.keys(fieldMapping).forEach(googleType => {
      const target = form.querySelector(fieldMapping[googleType]);
      if (target) { target.value = ''; dispatchInputEvents(target); }
    });
    dropdown.hide();
    sessionToken = new AutocompleteSessionToken();
  });
}

/**
 * Wait for the Google Maps SDK + Places library. Resolves immediately
 * if already loaded. If the SDK is already loading (e.g. dealer-locator
 * added the script first), don't add a second script tag — just poll
 * until `google.maps.places` is attached. Otherwise kick off our own
 * Loader (which also requests `places`) and let the same poll resolve.
 */
function loadGoogleMaps() {
  if (typeof google !== 'undefined' && google.maps && google.maps.places) {
    return Promise.resolve(google);
  }

  return new Promise((resolve, reject) => {
    let resolved = false;

    // If another module is mid-load (script tag already on the page),
    // skip instantiating a second Loader — Google's Web Components
    // throw "already defined" on the second registration.
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );

    if (!existingScript) {
      const loader = new Loader(API_KEY, {
        version: 'quarterly',
        libraries: ['places'],
        loading: 'async',
        language: PAGE_LANGUAGE,
      });
      loader.load().catch(() => { /* poll will time out and reject */ });
    }

    const start = Date.now();
    const poll = setInterval(() => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        clearInterval(poll);
        if (!resolved) { resolved = true; resolve(google); }
      } else if (Date.now() - start > POLL_TIMEOUT) {
        clearInterval(poll);
        if (!resolved) {
          resolved = true;
          reject(new Error('Google Maps API did not load within timeout'));
        }
      }
    }, POLL_INTERVAL);
  });
}

/**
 * Main initializer. Discovers Sitecore Forms + legacy data-attribute inputs
 * and attaches autocomplete to each. Safe to call multiple times — enhanced
 * inputs are marked and skipped on subsequent passes.
 */
function runDiscovery(google) {
  discoverSitecoreForms().forEach(f => enhanceSitecoreForm(google, f));
  document.querySelectorAll(SELECTOR_DATA).forEach(input => enhanceLegacyInput(google, input));
}

/**
 * Observe the DOM for Sitecore Forms AJAX re-renders. When the form submits
 * with validation errors, Sitecore replaces the <form> innerHTML — our handlers
 * die with it. Re-run discovery on any added <input>/<select> nodes.
 */
function watchForReinit(google) {
  let pending = null;
  const observer = new MutationObserver((mutations) => {
    const hasFieldChange = mutations.some(m =>
      Array.from(m.addedNodes).some(n =>
        n.nodeType === 1 && (
          n.matches('input, select, [data-sc-field-name], ' + SELECTOR_DATA) ||
          n.querySelector && n.querySelector('[data-sc-field-name], ' + SELECTOR_DATA)
        )
      )
    );
    if (!hasFieldChange) return;
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => runDiscovery(google));
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function hasEnhanceableFields() {
  return discoverSitecoreForms().length > 0
    || document.querySelector(SELECTOR_DATA) !== null;
}

function startWhenReady() {
  loadGoogleMaps().then(google => {
    runDiscovery(google);
    watchForReinit(google);
  }).catch(err => {
    console.warn('[hce-address-autocomplete] Google Places API failed to load. Fields remain manual.', err);
  });
}

// Skip the Maps SDK load entirely on pages with nothing to enhance
// (e.g. the dealer-locator page). Loading it here in addition to
// dealer-locator's own Loader puts two Maps SDK script tags on the
// page, and Google's internal Web Components throw "already defined"
// on the second registration — which kills the dealer locator.
// If a form appears later (Sitecore SPA nav, modal contact form),
// the observer triggers the load lazily.
function init() {
  if (hasEnhanceableFields()) {
    startWhenReady();
    return;
  }

  const observer = new MutationObserver(() => {
    if (!hasEnhanceableFields()) return;
    observer.disconnect();
    startWhenReady();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// Defer to next macrotask so Vue finishes mounting (Vue re-renders #app on
// init and would otherwise wipe our injected wrapper/dropdown elements).
function deferredInit() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 0));
  } else {
    setTimeout(init, 0);
  }
}

deferredInit();
